import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

export default function AdminRegisterPage() {
  const [form, setForm] = useState({ name: '', email: '', phone: '+44 ', password: '', role: 'admin' });
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:5001';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      const res = await fetch(`${API_BASE}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || 'Registration failed');
      }

      // Automatically login or redirect to login after successful registration
      navigate('/admin/login', { replace: true });
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex" style={{ backgroundColor: '#0a1a0f' }}>
      {/* Left branding panel */}
      <div className="hidden lg:flex lg:w-5/12 flex-col justify-between p-12 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-900/60 to-slate-900/80"></div>
        <div className="relative">
          <Link to="/" className="flex items-center gap-2 mb-16">
            <div className="w-9 h-9 flex items-center justify-center bg-emerald-600 rounded-lg">
              <i className="ri-computer-line text-white text-lg"></i>
            </div>
            <span className="font-bold text-lg text-white" style={{ fontFamily: "'Inter', sans-serif" }}>
              GCSE <span className="text-emerald-400">Computing</span>
            </span>
          </Link>
          <div className="mb-8">
            <div className="w-14 h-14 flex items-center justify-center bg-emerald-700/30 rounded-2xl mb-6">
              <i className="ri-user-add-line text-emerald-400 text-3xl"></i>
            </div>
            <h1 className="text-3xl font-extrabold text-white mb-3" style={{ fontFamily: "'Inter', sans-serif" }}>
              Create Admin Account
            </h1>
            <p className="text-slate-400 text-sm leading-relaxed">
              Register to manage students, tutors, inquiries, events and all platform content.
            </p>
          </div>
        </div>
        <p className="relative text-slate-600 text-xs">
          &copy; 2026 GCSE Computing · Admin Access Only
        </p>
      </div>

      {/* Right register form */}
      <div className="flex-1 flex items-center justify-center px-6 py-12 bg-white">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-2 mb-10">
            <div className="w-8 h-8 flex items-center justify-center bg-emerald-600 rounded-lg">
              <i className="ri-computer-line text-white text-base"></i>
            </div>
            <span className="font-bold text-lg text-white text-slate-900" style={{ fontFamily: "'Inter', sans-serif" }}>
              GCSE <span className="text-emerald-700">Computing</span>
            </span>
          </div>

          <div className="mb-8">
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-emerald-700 bg-emerald-50 border border-emerald-200 px-3 py-1 rounded-full mb-4">
              <i className="ri-shield-line text-xs"></i> Admin Access
            </span>
            <h2 className="text-2xl font-bold text-slate-800 mb-1" style={{ fontFamily: "'Inter', sans-serif" }}>
              Register Admin Account
            </h2>
            <p className="text-slate-500 text-sm">Authorised personnel only.</p>
          </div>

          {error && (
            <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-lg mb-5">
              <i className="ri-error-warning-line flex-shrink-0"></i>
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Full Name</label>
              <input
                type="text" required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Admin Name"
                className="w-full border border-slate-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-emerald-500 transition-colors"
                disabled={isSubmitting}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Admin Email</label>
              <input
                type="email" required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="admin@computinghub.co.uk"
                className="w-full border border-slate-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-emerald-500 transition-colors"
                disabled={isSubmitting}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Phone Number</label>
              <input
                type="tel"
                value={form.phone}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val.startsWith('+44 ')) {
                    setForm({ ...form, phone: val });
                  } else if (val.length < 4) {
                    setForm({ ...form, phone: '+44 ' });
                  }
                }}
                placeholder="+44 7700 000000"
                maxLength={15}
                className="w-full border border-slate-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-emerald-500 transition-colors"
                disabled={isSubmitting}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Password</label>
              <div className="relative">
                <input
                  type={showPass ? 'text' : 'password'} required
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  placeholder="••••••••"
                  className="w-full border border-slate-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-emerald-500 transition-colors pr-10"
                  disabled={isSubmitting}
                />
                <button type="button" onClick={() => setShowPass(!showPass)} className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-slate-400 cursor-pointer">
                  <i className={showPass ? 'ri-eye-off-line' : 'ri-eye-line'}></i>
                </button>
              </div>
            </div>
            <button
              type="submit"
              disabled={isSubmitting}
              className={`bg-emerald-700 hover:bg-emerald-800 text-white font-semibold py-3 rounded-lg transition-colors cursor-pointer whitespace-nowrap text-sm mt-2 ${isSubmitting ? 'opacity-70 cursor-not-allowed' : ''}`}
            >
              {isSubmitting ? 'Registering...' : 'Register Admin Account'}
            </button>
          </form>

          <div className="mt-8 pt-6 border-t border-slate-100 flex items-center justify-between">
            <Link to="/admin/login" className="text-sm text-emerald-700 hover:text-emerald-800 font-medium cursor-pointer">
              Admin login instead &rarr;
            </Link>
            <Link to="/" className="text-sm text-slate-400 hover:text-slate-600 cursor-pointer">
              Back to site
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
